import { FC } from 'react';
import { round } from 'lodash';
import { differenceInDays, startOfDay } from 'date-fns';
import Typography from '@mui/material/Typography';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { styled } from '@mui/material/styles';
import { Row, Column } from 'further-ui/components/Layout';
import { CurrencyCode } from 'further-types/lp/vehicle';
import {
  CalendarDay,
  numberToCurrencyString,
  pluralize,
} from 'further-ui/utils';
import CapitalProgress from './CapitalProgress';

const CapitalProgressContainer = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'width',
})<{ width: number | null }>(({ theme, width }) => ({
  padding: theme.spacing(theme.spacingSizes.sm, theme.spacingSizes.xl),
  width: width ? `${width - 2}px` : '100%',
}));

type Props = {
  capitalAction: {
    actionDueDate: CalendarDay;
    investorsPaid: number;
    totalInvestors: number;
    amountPaid: number;
    totalAmount: number;
    isPaid: boolean;
    perInvestorBreakdown: Array<{
      id: string;
      fullName: string;
      totalCommitmentLcy: number;
      capitalDistributedLcy: number;
      capitalCalledLcy: number;
      amountPaidLcy: number;
      advanceSubscriptionUsedLcy: number;
    }>;
  };
  tableWidth: number | null;
  baseCurrency?: CurrencyCode;
};

const CapitalActionExpandedContent: FC<Props> = ({
  capitalAction,
  tableWidth,
  baseCurrency,
}) => {
  const {
    actionDueDate,
    investorsPaid,
    totalInvestors,
    amountPaid,
    totalAmount,
    isPaid,
  } = capitalAction;
  const dueDate = actionDueDate.toUTCDate();
  const today = startOfDay(new Date());
  const daysUntilDue = differenceInDays(dueDate, today);
  const daysUntilDueColor = daysUntilDue <= 7 ? 'error' : 'grey.900';
  const daysUntilDueText =
    daysUntilDue > 0
      ? `Due in ${daysUntilDue} ${pluralize('day', daysUntilDue)}`
      : daysUntilDue === 0
      ? 'Due today'
      : 'Overdue';
  const progress =
    totalAmount > 0 ? round((amountPaid / totalAmount) * 100, 2) : 0;

  const hasAdvanceSubscriptionUsed = capitalAction.perInvestorBreakdown.some(
    (item) => item.advanceSubscriptionUsedLcy !== 0,
  );

  const columns: Array<
    TableColumn<(typeof capitalAction.perInvestorBreakdown)[number]>
  > = [
    {
      key: 'fullName',
      weight: 0.3,
      label: 'Investor',
      renderAvatar: ({ fullName: text }) => ({ text }),
    },
    {
      key: 'capitalCalledLcy',
      weight: 0.2,
      label: 'Capital call',
      render: ({ capitalCalledLcy }) =>
        numberToCurrencyString(capitalCalledLcy, {
          currency: baseCurrency,
        }),
    },
    {
      key: 'capitalDistributedLcy',
      weight: 0.2,
      label: 'Distribution / capital refund',
      render: ({ capitalDistributedLcy }) =>
        numberToCurrencyString(capitalDistributedLcy, {
          currency: baseCurrency,
        }),
    },
    ...(hasAdvanceSubscriptionUsed
      ? [
          {
            key: 'advanceSubscriptionUsedLcy',
            label: 'Advance subscription used',
            render: ({ advanceSubscriptionUsedLcy }) =>
              numberToCurrencyString(advanceSubscriptionUsedLcy, {
                currency: baseCurrency,
              }),
          },
        ]
      : []),
    {
      key: 'totalDueLcy',
      weight: 0.2,
      label: 'Total due to / (from) fund',
      render: ({ capitalCalledLcy, capitalDistributedLcy }) => {
        const totalDueLcy = capitalCalledLcy - capitalDistributedLcy;
        return numberToCurrencyString(totalDueLcy, {
          currency: baseCurrency,
        });
      },
    },
    {
      key: 'amountPaidLcy',
      weight: 0.2,
      label: 'Amount paid',
      render: ({ amountPaidLcy }) =>
        numberToCurrencyString(amountPaidLcy, { currency: baseCurrency }),
    },
  ];

  return (
    <CapitalProgressContainer spacing="sm" width={tableWidth}>
      <Typography variant="h6">{`${numberToCurrencyString(amountPaid, {
        currency: baseCurrency,
      })} of ${numberToCurrencyString(totalAmount, {
        currency: baseCurrency,
      })}`}</Typography>
      <CapitalProgress value={progress} />
      <Row spacing="sm" justify="space-between">
        <Typography variant="h6" color="grey.600">
          {`${investorsPaid}/${totalInvestors} investors paid`}
        </Typography>
        {isPaid ? null : (
          <Typography variant="h6" color={daysUntilDueColor}>
            {daysUntilDueText}
          </Typography>
        )}
      </Row>
      <Table
        showTopBorder
        showHeaderBorder
        rows={capitalAction.perInvestorBreakdown}
        columns={columns}
      />
    </CapitalProgressContainer>
  );
};

export default CapitalActionExpandedContent;
